/**
 * Section for displaying and editing ticket revenue line items
 */

import React from 'react';
import { Box, Typography, Divider, Button, Stack, Chip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import type { TicketRevenueItem } from '../../../types/TicketRevenueItem';
import { formatCurrency } from '../../../utils/formatters';
import { TicketRevenueItemForm } from '../forms/TicketRevenueItemForm';
import { FinancialLine } from './FinancialLine';

interface TicketRevenueSectionProps {
  ticketRevenueItems: TicketRevenueItem[];
  totalTicketRevenue: number;
  isEditing: boolean;
  isDatePast: boolean;
  formData: Record<string, unknown>;
  onUpdateField: (field: string, value: unknown) => void;
  onStartEdit?: (section: string, holdId: string) => Promise<void>;
  holdId?: string;
}

export const TicketRevenueSection: React.FC<TicketRevenueSectionProps> = ({
  ticketRevenueItems,
  totalTicketRevenue,
  isEditing,
  isDatePast,
  formData,
  onUpdateField,
  onStartEdit,
  holdId,
}) => {
  const handleAddItem = () => {
    const currentItems = (formData.ticketRevenueItems as TicketRevenueItem[]) || ticketRevenueItems;
    onUpdateField('ticketRevenueItems', [
      ...currentItems,
      { type: 'door', name: '', quantity: 0, price: 0 },
    ]);
  };

  const handleUpdateItem = (index: number, updatedItem: TicketRevenueItem) => {
    const currentItems = (formData.ticketRevenueItems as TicketRevenueItem[]) || ticketRevenueItems;
    const newItems = [...currentItems];
    newItems[index] = updatedItem;
    onUpdateField('ticketRevenueItems', newItems);
  };

  const handleRemoveItem = (index: number) => {
    const currentItems = (formData.ticketRevenueItems as TicketRevenueItem[]) || ticketRevenueItems;
    const newItems = currentItems.filter((_, i) => i !== index);
    onUpdateField('ticketRevenueItems', newItems);
  };

  const displayItems = isEditing
    ? ((formData.ticketRevenueItems as TicketRevenueItem[]) || ticketRevenueItems)
    : ticketRevenueItems;

  // Calculate subtotal
  const subtotal = displayItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.price || 0),
    0
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Ticket Revenue (This Date)
      </Typography>

      {isEditing ? (
        // Edit mode
        <Stack spacing={2}>
          {/* Tickets go on sale well before the show, so advance sales are
              editable on a future date. Bar revenue and attendance are not —
              those are only knowable on the night. */}
          {!isDatePast && (
            <Typography variant="caption" color="text.secondary">
              Advance ticket sales can be recorded before the show.
            </Typography>
          )}

          {displayItems.map((item, index) => (
            <TicketRevenueItemForm
              key={index}
              item={item}
              onChange={(updated) => handleUpdateItem(index, updated)}
              onRemove={() => handleRemoveItem(index)}
            />
          ))}

          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            ADD TICKET LINE ITEM
          </Button>

          <Divider sx={{ my: 2 }} />

          <FinancialLine label="Subtotal:" value={formatCurrency(subtotal)} total />
        </Stack>
      ) : (
        // View mode
        <Box
          onClick={() => {
            if (onStartEdit && holdId) {
              void onStartEdit('financial', holdId);
            }
          }}
          sx={{
            cursor: onStartEdit ? 'pointer' : 'default',
            '&:hover': onStartEdit ? {
              bgcolor: 'action.hover',
              borderRadius: 1,
            } : {},
            p: 1,
            mx: -1,
          }}
        >
          {displayItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No ticket revenue recorded
            </Typography>
          ) : (
            <Stack spacing={1}>
              {displayItems.map((item, index) => (
                <FinancialLine key={index} value={formatCurrency((item.quantity || 0) * (item.price || 0))}
                  label={<Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }} alignItems="baseline">
                    <Chip label={item.type.charAt(0).toUpperCase() + item.type.slice(1)} size="small" />
                    <Box component="span">
                      {item.name || 'Unnamed'}: {item.quantity} × {formatCurrency(item.price || 0)}
                    </Box>
                  </Stack>} />
              ))}

              <Divider sx={{ my: 2 }} />
              
              <FinancialLine label="Ticket Revenue Subtotal:" value={formatCurrency(totalTicketRevenue)} total />
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
